import * as store from '@attain-apps/fe/web/main/lib/store'
import * as rxjs from 'rxjs'

import type { Collection } from '../../../../collection'
import type * as ecModels from '../../../../models'
import type { Plugin } from '../plugin'

/**
 * When we finish editing (submit or discard), if that entity is the current
 * entity, we want to unset that as the current entity
 */
export const unsetCurrentEntityWhenDoneEditing = store.effects.create(
  <TTypeParams extends ecModels.TypeParams.Constraint>(
    feature: Plugin<TTypeParams>['feature'],
    collection: Collection<TTypeParams>,
  ) =>
    new rxjs.Observable(subscriber => {
      const cleanUpSubscriptions: Partial<Record<string, rxjs.Subscription>> =
        {}

      /**
       * Listens to each current entity target, and removes that target when we
       * stop editing
       */
      const sourceSubscription =
        collection.plugins.currentEntityPlugin.storage.selectors.idLookupKeys.asObservable.subscribe(
          {
            complete: () => subscriber.complete(),
            error: error => subscriber.error(error),
            next: currentTargets => {
              // First, we go through all the targets we were previously
              // tracking
              for (const oldTarget in cleanUpSubscriptions) {
                if (!currentTargets.includes(oldTarget)) {
                  // If we find a target that is no longer in use, we
                  // unsubscribe from and remove the cleanup subscription on
                  // that target
                  cleanUpSubscriptions[oldTarget]?.unsubscribe()
                  delete cleanUpSubscriptions[oldTarget]
                }
              }
              for (const currentTarget of currentTargets) {
                // For all current targets, if we do not already have a cleanup
                // subscription, we create one
                cleanUpSubscriptions[currentTarget] ??=
                  // First, we listen to the ID on this target
                  collection.plugins.currentEntityPlugin.feature.selectors
                    .getId({ target: currentTarget })
                    .asObservable.pipe(
                      // Any time the ID changes, we forget about the previous
                      // ID and immediately start listening to the new ID (hence
                      // the __switch__Map)
                      rxjs.switchMap(id =>
                        // As long as there is no current ID, we don't do
                        // anything
                        id === undefined
                          ? rxjs.EMPTY
                          : // Once there is a current ID...
                            rxjs
                              .merge(
                                feature.actions.submit.success.observe(),
                                feature.actions.discard.success.observe(),
                              )
                              .pipe(
                                // If we encounter a case where the entity is
                                // discarded or submitted
                                rxjs.filter(action => {
                                  const { request, response } = action.data

                                  // Skip unsetting if explicitly requested via
                                  // skipUnset flag
                                  if (request.skipUnset) {
                                    return false
                                  }
                                  // Only unset if the current entity ID was
                                  // affected
                                  const affectedIds = [
                                    ...response.creations.map(
                                      creation => creation.id,
                                    ),
                                    ...response.updates.map(
                                      update => update.id,
                                    ),
                                    ...response.deletions,
                                  ]
                                  // Proceed to unset current entity if it's in
                                  // the affected IDs list
                                  return affectedIds.includes(id)
                                }),
                                rxjs.map(() =>
                                  // Then we unset this current entity
                                  collection.plugins.currentEntityPlugin.storage.actions.putInIdLookup
                                    .from(
                                      'unsetCurrentEntityWhenDoneEditing (effect)',
                                    )
                                    .create({
                                      key: currentTarget,
                                      value: undefined,
                                    }),
                                ),
                              ),
                      ),
                    )
                    .subscribe(action => subscriber.next(action))
              }
            },
          },
        )
      // Unsubscribing
      return {
        unsubscribe: () => {
          sourceSubscription.unsubscribe()
          for (const key in cleanUpSubscriptions) {
            cleanUpSubscriptions[key]?.unsubscribe()
          }
        },
      }
    }),
)
